import type { NumberFormatOptionsType } from '../types';

import { formatNumber } from '../formatNumber';
import { toFixed } from '../utils';

const enum NumberSet {
  THOUSANDS = 'thousands',
  MILLIONS = 'millions',
  BILLIONS = 'billions',
}

type NumberSetMap<T> = { [key in NumberSet]: T };

type Options = {
  startValue?: number;
  upperCase?: boolean;
  accuracy?: Partial<NumberSetMap<number>>;
  formatOptions?: NumberFormatOptionsType;
};

const TRUNCATIONS: NumberSetMap<string> = {
  [NumberSet.THOUSANDS]: 'k',
  [NumberSet.MILLIONS]: 'kk',
  [NumberSet.BILLIONS]: 'kkk',
};

const DIVISORS: NumberSetMap<number> = {
  [NumberSet.THOUSANDS]: 1000,
  [NumberSet.MILLIONS]: 1000000,
  [NumberSet.BILLIONS]: 1000000000,
};

const getNumberSet = (value: number, startValue: number) => {
  switch (true) {
    case value < startValue:
      return undefined;
    case value >= DIVISORS[NumberSet.BILLIONS]:
      return NumberSet.BILLIONS;
    case value >= DIVISORS[NumberSet.MILLIONS]:
      return NumberSet.MILLIONS;
    case value >= DIVISORS[NumberSet.THOUSANDS]:
      return NumberSet.THOUSANDS;
    default:
      return undefined;
  }
};

const DEFAULT_OPTIONS = {
  startValue: 1000,
  upperCase: false,
  accuracy: {
    [NumberSet.THOUSANDS]: 0,
    [NumberSet.MILLIONS]: 1,
    [NumberSet.BILLIONS]: 1,
  },
};

export const getTruncatedNumber = (
  value: number,
  options?: Options,
) => {
  const startValue = options?.startValue || DEFAULT_OPTIONS.startValue;
  const upperCase = options?.upperCase || DEFAULT_OPTIONS.upperCase;
  const accuracy = { ...DEFAULT_OPTIONS.accuracy, ...options?.accuracy };
  const formatOptions = options?.formatOptions;
  const numberSet = getNumberSet(value, startValue);

  if (!numberSet) {
    return formatOptions ? formatNumber(value, formatOptions) : String(value);
  }

  const truncation = upperCase ? TRUNCATIONS[numberSet].toUpperCase() : TRUNCATIONS[numberSet];

  return `${parseFloat(toFixed(value / DIVISORS[numberSet], accuracy[numberSet]).toFixed(accuracy[numberSet]))}${truncation}`;
};
